/**
 * GameTopBar — the thin in-game navigation bar shown during ACTIVE/COMPLETE.
 *
 * Layout (ACTIVE): [ ☰ menu ] … [ players dots ]
 *
 *   • Menu (GameMenu, a rounded-square hamburger) → dice luck,
 *     config, settings, resign, back-to-lobby.
 *   • Players dots (ACTIVE only) → opens the PlayerRosterPanel details drawer.
 *   • Chat / log toggles: phones use the map controls bar; desktop shows them
 *     right after the menu button (`leadingControls`).
 *   • COMPLETE: "← Back", final results, Dice Luck and the game-log button
 *     (no menu); no player dots.
 *
 * Rendered in both orientations: portrait puts it in a top header row; landscape
 * renders it inside the action-panel header slot (so it inherits that column's
 * safe-area padding — no floating-over-the-map notch bugs).
 */

import React from 'react'
import { View, Text, TouchableOpacity, type ViewStyle, type TextStyle } from 'react-native'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import { GameMenu } from './GameMenu'
import { mapGameLayout } from '../../lib/mapGameLayout'
import { PlayerDotsStrip } from './PlayerDotsStrip'
import { GameBanner } from './GameBanner'
import { CountBubble } from '../ui/CountBubble'
import { useTheme } from '../../hooks/useTheme'
import { useNow } from '../../hooks/useNow'
import { overdueBootTarget } from '../../lib/bootAffordance'
import type { GameState } from '../../lib/gameState'

// Ink colour per theme (mirrors the `text-ink` token) for tinting the
// monochrome vector icons dark on light / light on dark.
const INK = { light: '#18170f', dark: '#e6e1c4' } as const
import type { GamePlayerResponse } from '@warcarp/api-types'

// Re-exported so existing importers (chat) keep working; the
// source of truth is lib/gameChrome to avoid a PlayerRosterPanel ↔ GameTopBar
// import cycle.
export { GAME_TOP_BAR_HEIGHT } from '../../lib/gameChrome'
import { GAME_TOP_BAR_HEIGHT } from '../../lib/gameChrome'

/** Shared 38×38 circular icon-button chrome (matches the legacy cog). */
function IconButton({
  glyph,
  label,
  onPress,
  active,
  badgeCount,
  glyphStyle,
  icon,
  testID,
}: {
  glyph: string
  label: string
  onPress: () => void
  active?: boolean
  /** When > 0, renders a blood count badge (mirrors the cards HUD). */
  badgeCount?: number
  /** Explicit glyph sizing. The ⚙ symbol needs a bump (and a line-height
   *  matched to its font size) to both fill the button like the emoji glyphs
   *  and sit vertically centred. */
  glyphStyle?: TextStyle
  /** Render a custom node (e.g. a monochrome vector icon) instead of the text
   *  glyph — used where a tinted, guaranteed-dark icon is needed in light mode. */
  icon?: React.ReactNode
  testID?: string
}) {
  return (
    <TouchableOpacity
      onPress={onPress}
      accessibilityLabel={label}
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
      testID={testID}
      className={`border border-olive rounded-full items-center justify-center ${
        active ? 'bg-blood' : 'bg-paper'
      }`}
      style={{ width: 38, height: 38, position: 'relative', flexShrink: 0 }}
    >
      {icon ?? (
        <Text
          className={active ? 'text-white text-base' : 'text-ink text-base'}
          style={glyphStyle}
          allowFontScaling={false}
        >
          {glyph}
        </Text>
      )}
      {badgeCount != null && badgeCount > 0 && (
        <View pointerEvents="none" style={{ position: 'absolute', top: -3, right: -3 }}>
          <CountBubble count={badgeCount} />
        </View>
      )}
    </TouchableOpacity>
  )
}

export function GameTopBar({
  gameId,
  status,
  state,
  players,
  presence,
  canResign,
  tutorialEnabled = false,
  onTutorialEnabledChange = () => {},
  onShowDiceLuck,
  onShowResults,
  dmUnreadTotal = 0,
  showActivity = true,
  activityOpen,
  playersActive,
  onOpenPlayers,
  onToggleActivity,
  onBack,
  leadingControls,
  playersAlignRight = false,
  style,
  testIDSuffix = '',
  connectionNotice,
  overMap = false,
  bootableAt,
  bootAfterSeconds,
  viewerUserId,
}: {
  gameId: string
  bootableAt?: string | null
  bootAfterSeconds?: number
  viewerUserId?: string
  status: 'ACTIVE' | 'COMPLETE'
  state: GameState | null
  players: GamePlayerResponse[]
  presence?: Set<string>
  canResign: boolean
  tutorialEnabled?: boolean
  onTutorialEnabledChange?: (enabled: boolean) => void
  onShowDiceLuck?: () => void
  /** COMPLETE only: reopen the final results (Game Over) modal. */
  onShowResults?: () => void
  /** Total unread secret (direct) messages — drives a badge on the player dots. */
  dmUnreadTotal?: number
  /** Hide the logs control when a completed game's event history was purged. */
  showActivity?: boolean
  activityOpen: boolean
  /** True while the player-details drawer is open — the dots pill shows active. */
  playersActive?: boolean
  onOpenPlayers: () => void
  onToggleActivity: () => void
  onBack: () => void
  /** Desktop: chat / log toggles shown right after the menu button. */
  leadingControls?: React.ReactNode
  /**
   * Force the player strip to the right, ignoring a map's centred player bar.
   * The wide (desktop / landscape) bar does: its column is narrow, and the
   * strip reads as part of the bar's right edge. Portrait keeps the map's choice.
   */
  playersAlignRight?: boolean
  style?: ViewStyle
  testIDSuffix?: string
  connectionNotice?: string
  overMap?: boolean
}) {
  const [leadingWidth, setLeadingWidth] = React.useState(38)
  const centeredPlayers = !playersAlignRight && !!state && mapGameLayout(state.map).playerBarPosition === 'center'
  const isComplete = status === 'COMPLETE'
  const now = useNow(bootableAt && !isComplete ? 1000 : null)
  const bootTarget = overdueBootTarget({ state, players, viewerUserId, bootableAt, bootAfterSeconds: bootAfterSeconds ?? 432000, now })
  const { theme } = useTheme()
  const inkColor = INK[theme === 'dark' ? 'dark' : 'light']
  // Optional per map (MapDef.backdropColor.topBar): the bar takes the map's backdrop colour.
  const sea = state?.map.backdropColor
  const backdropBar = !overMap && sea?.topBar ? { backgroundColor: sea[theme === 'dark' ? 'dark' : 'light'] } : undefined
  return (
    <View pointerEvents={overMap ? 'box-none' : 'auto'}>
    <View
      pointerEvents={overMap ? 'box-none' : 'auto'}
      // Paper fill + bottom rule matches the map controls bar and gives the
      // nav bar a clear edge against the map below (esp. in light mode, where
      // a transparent bar on the canvas washed out).
      // No bottom rule: the bar sits flush on the map (portrait) / log column.
      className={`flex-row items-center gap-2 px-2 ${overMap ? 'bg-transparent' : 'bg-paper'}`}
      accessibilityLabel="Game top bar"
      // px-2 (8 px) left inset = the turn panels' (attack / fortify / …)
      // inset, so the menu button lines up with their left edge.
      style={[{ height: GAME_TOP_BAR_HEIGHT }, backdropBar, style]}
    >
      <View onLayout={event => setLeadingWidth(event.nativeEvent.layout.width)} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexShrink: 0 }}>
      {/* Left end: the game menu button (sized like the chat send button). */}
      {!isComplete && (
        <View style={{ flexShrink: 0 }}>
        <GameMenu
          gameId={gameId}
          canResign={canResign}
          hasSpecialRules={!!state?.map.specialRules?.length}
          tutorialEnabled={tutorialEnabled}
          onTutorialEnabledChange={onTutorialEnabledChange}
          gameState={state}
          playerCount={players.length}
          bootAfterSeconds={bootAfterSeconds}
          onShowDiceLuck={onShowDiceLuck}
          trigger={(open, attentionCount) => (
            // Game menu: a slightly rounded square in the map controls' pad
            // style (paper fill, olive rule, ink glyph) — not a round icon.
            <TouchableOpacity
              onPress={open}
              accessibilityLabel="Open game menu"
              accessibilityRole="button"
              testID={`game-cog${testIDSuffix}`}
              className="bg-paper border border-olive rounded-md items-center justify-center"
              style={{ width: 30, height: 30, position: 'relative', flexShrink: 0 }}
            >
              <MaterialCommunityIcons name="menu" size={20} color={inkColor} />
              {attentionCount > 0 && (
                // Zero-size anchor at the button's outer corner (1px border),
                // a smidge in (down and left): the bubble centres on it,
                // whatever the number's width.
                <View
                  pointerEvents="none"
                  style={{ position: 'absolute', top: 1, right: 1, width: 0, height: 0, alignItems: 'center', justifyContent: 'center', overflow: 'visible' }}
                >
                  <CountBubble count={attentionCount} />
                </View>
              )}
            </TouchableOpacity>
          )}
        />
        </View>
      )}
      {leadingControls}
      {/* ACTIVE: game menu button on the left, player dots on the right.
          COMPLETE leads with ← Back, then results, Dice Luck, logs. */}
      {isComplete ? (
        <TouchableOpacity
          onPress={onBack}
          accessibilityLabel="Go back"
          accessibilityRole="button"
          testID={`game-back${testIDSuffix}`}
          className="p-1"
          style={{ flexShrink: 0 }}
        >
          <Text className="text-blood text-base font-typed">← Back</Text>
        </TouchableOpacity>
      ) : null}
      {isComplete && onShowResults && (
        <IconButton
          glyph="🏆"
          icon={<MaterialCommunityIcons name="trophy" size={19} color={inkColor} />}
          label="Show final results"
          onPress={onShowResults}
          testID={`game-results${testIDSuffix}`}
        />
      )}
      {isComplete && onShowDiceLuck && (
        <IconButton
          glyph="⚄"
          icon={<MaterialCommunityIcons name="dice-multiple" size={21} color={inkColor} />}
          label="Open dice luck"
          onPress={onShowDiceLuck}
          testID={`game-dice-luck${testIDSuffix}`}
        />
      )}
      {/* Games with a chat bar grow the log from its controls. This button is
          only for a finished game viewed without one (spectators, landscape
          phones); the screen hides it otherwise via showActivity. */}
      {showActivity && isComplete && (
        <IconButton
          glyph="📜"
          icon={
            <MaterialCommunityIcons
              name="script-text"
              size={19}
              color={activityOpen ? '#fff' : inkColor}
            />
          }
          label={activityOpen ? 'Shrink the game log' : 'Open the game log'}
          onPress={onToggleActivity}
          active={activityOpen}
          testID={`game-activity${testIDSuffix}`}
        />
      )}

      </View>
      {/* Right: compact player dots (ACTIVE only; the finished board is shown
          alone). The strip fills the remaining row width (flex:1) and
          right-aligns its pill, shrinking the dots to fit a narrow column so
          the last dot is never clipped. Tap opens the player details drawer. */}
      {!isComplete && state && (
        <View pointerEvents="box-none" style={centeredPlayers ? { position: 'absolute', left: leadingWidth + 16, right: leadingWidth + 16 } : { flex: 1, minWidth: 0 }}>
        <PlayerDotsStrip
          overdueUserId={bootTarget?.userId}
          alignment={centeredPlayers ? 'center' : 'right'}
          state={state}
          players={players}
          presence={presence}
          onOpen={onOpenPlayers}
          active={playersActive}
          unreadTotal={dmUnreadTotal}
        />
        </View>
      )}

    </View>
    {!!connectionNotice && <GameBanner message={connectionNotice} spinner tone="info" topInset={0} />}
    </View>
  )
}
